import type { ExcursionBooking } from "api/excursions/Excursion.typed";
import type { LeaveRequest } from "api/leave/Leave.typed";
import { LeaveStatus } from "api/leave/Leave.typed";

export type SlotConflictKind = "leave" | "booking" | "same_excursion_group";

/** Matches API-style excursion windows: local calendar day + optional HH:mm (defaults like list filters). */
export function parseExcursionSlotBounds(
  dateYmd: string,
  startTimeRaw?: string | null,
  endTimeRaw?: string | null,
): { start: Date; end: Date } | null {
  const ymd = dateYmd.trim().slice(0, 10);
  const pr = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!pr) return null;
  const y = parseInt(pr[1], 10);
  const mo = parseInt(pr[2], 10);
  const d = parseInt(pr[3], 10);
  if (!y || !mo || !d) return null;
  const startM = parseHHMM(startTimeRaw) ?? 0;
  const endM = parseHHMM(endTimeRaw) ?? parseHHMM(startTimeRaw) ?? 23 * 60 + 59;
  const sh = Math.floor(startM / 60);
  const smin = startM % 60;
  const eh = Math.floor(endM / 60);
  const emin = endM % 60;
  const start = new Date(y, mo - 1, d, sh, smin, 0, 0);
  const end = new Date(y, mo - 1, d, eh, emin, 0, 0);
  if (end.getTime() <= start.getTime()) {
    return { start, end: new Date(y, mo - 1, d, 23, 59, 59, 999) };
  }
  return { start, end };
}

function parseHHMM(s?: string | null): number | null {
  if (s == null || !String(s).trim()) return null;
  const m = String(s)
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59 || h < 0 || min < 0) return null;
  return h * 60 + min;
}

function intervalsOverlap(a0: Date, a1: Date, b0: Date, b1: Date): boolean {
  return a0.getTime() < b1.getTime() && b0.getTime() < a1.getTime();
}

function leaveOverlapsSlot(
  leave: LeaveRequest,
  slot: { start: Date; end: Date },
  excursionYmd: string,
  companyId: number,
): boolean {
  if (leave.company_id != null && leave.company_id !== companyId) return false;
  if (leave.status !== LeaveStatus.PENDING && leave.status !== LeaveStatus.APPROVED) return false;
  const lStart = leave.start_date?.trim().slice(0, 10);
  const lEnd = leave.end_date?.trim().slice(0, 10);
  if (!lStart || !lEnd) return false;
  if (leave.start_at && leave.end_at) {
    const u0 = new Date(leave.start_at);
    const u1 = new Date(leave.end_at);
    if (Number.isNaN(u0.getTime()) || Number.isNaN(u1.getTime())) return false;
    return intervalsOverlap(slot.start, slot.end, u0, u1);
  }
  return excursionYmd >= lStart && excursionYmd <= lEnd;
}

function bookingOverlapsSlot(
  booking: ExcursionBooking,
  slot: { start: Date; end: Date },
  excursionYmd: string,
  companyId: number,
  excludeExcursionId?: number | null,
): boolean {
  if (booking.company_id !== companyId) return false;
  if (booking.status === "rejected" || booking.status === "cancelled") return false;
  if (excludeExcursionId != null && booking.excursion_id === excludeExcursionId) return false;
  const bYmd = booking.date?.trim().slice(0, 10);
  if (!bYmd || bYmd !== excursionYmd) return false;
  const bSlot = parseExcursionSlotBounds(bYmd, booking.start_time, booking.end_time);
  if (!bSlot) return false;
  return intervalsOverlap(slot.start, slot.end, bSlot.start, bSlot.end);
}

export function findUserSlotConflict(args: {
  userId: number;
  companyId: number;
  excursionYmd: string;
  slot: { start: Date; end: Date } | null;
  leaves: LeaveRequest[];
  bookings: ExcursionBooking[];
  excludeExcursionId?: number | null;
  assignedElsewhereInForm: boolean;
}): SlotConflictKind | null {
  if (args.assignedElsewhereInForm) return "same_excursion_group";
  if (args.slot == null) return null;
  for (const l of args.leaves) {
    if (l.user_id !== args.userId) continue;
    if (leaveOverlapsSlot(l, args.slot, args.excursionYmd, args.companyId)) return "leave";
  }
  for (const b of args.bookings) {
    if (b.user_id !== args.userId) continue;
    if (
      bookingOverlapsSlot(b, args.slot, args.excursionYmd, args.companyId, args.excludeExcursionId)
    )
      return "booking";
  }
  return null;
}

export function slotConflictHint(kind: SlotConflictKind): string {
  switch (kind) {
    case "leave":
      return "Congé ou indisponibilité sur ce créneau";
    case "booking":
      return "Autre prestation sur ce créneau";
    case "same_excursion_group":
      return "Déjà affecté à un autre groupe de cette sortie";
    default:
      return "Indisponible sur ce créneau";
  }
}

/** First / last calendar day of the month containing `dateYmd` (YYYY-MM-DD). */
export function monthRangeContainingYmd(dateYmd: string): { from: string; to: string } | null {
  const ymd = dateYmd.trim().slice(0, 10);
  const pr = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!pr) return null;
  const y = parseInt(pr[1], 10);
  const mo = parseInt(pr[2], 10);
  if (!y || !mo) return null;
  const lastDay = new Date(y, mo, 0).getDate();
  const from = `${pr[1]}-${pr[2]}-01`;
  const to = `${pr[1]}-${pr[2]}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
}
